import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Droplets, CheckCircle2, Loader2, Eye, CalendarCheck } from "lucide-react";
import api from "@/lib/api";
import { pushFormEvent } from "@/lib/analytics";
import { toast } from "sonner";

const CONCERN_OPTS = [
  ["taste_smell", "Taste or smell"],
  ["colour_staining", "Colour, staining or cloudy water"],
  ["unsure_last_tested", "Not sure when it was last tested or cleaned"],
  ["no_specific", "No specific concern — just want it checked"],
  ["other", "Something else"],
];

// Public QR landing — mobile-first, no site nav, noindex.
// ?preview=1 + a valid ADMIN session uses the authenticated preview
// endpoint (no scan tracking). Without admin auth it falls back to the
// normal tracked public endpoint, so the param can't be abused.
export default function PropertyCheck() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const wantPreview = searchParams.get("preview") === "1";
  const fetchedFor = useRef(null);
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading|ready|ask_occupancy|tenant|concerns|answered_yes|answered_no|lead_done|choose_slot|booked|notfound
  const [form, setForm] = useState({ first_name: "", last_name: "", mobile: "", email: "" });
  const [concerns, setConcerns] = useState([]);
  const [otherText, setOtherText] = useState("");
  const [slots, setSlots] = useState([]);
  const [selDate, setSelDate] = useState(null);
  const [showAllDates, setShowAllDates] = useState(false);
  const [selSlot, setSelSlot] = useState(null);
  const [ebookUrl, setEbookUrl] = useState(null);
  const [appt, setAppt] = useState(null);
  const [resched, setResched] = useState(false);
  const [busy, setBusy] = useState(false);
  const isPreview = !!data?.preview;

  const loadSlots = useCallback(async () => {
    try {
      const { data: s } = await api.get(`/public/check/${token}/slots`);
      setSlots(s.slots || []);
      setSelDate(null); setSelSlot(null); setShowAllDates(false);
      setState((s.slots || []).length ? "choose_slot" : "lead_done");
    } catch { setState("lead_done"); }
  }, [token]);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots"; meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    const applyData = (d, allowSlots) => {
      setData(d);
      if (d.ebook_url) setEbookUrl(d.ebook_url);
      if (d.appointment) { setAppt(d.appointment); setState(d.appointment.status === "completed" ? "completed" : "booked"); }
      else if (d.lead_captured) { allowSlots ? loadSlots() : setState("lead_done"); }
      else if (d.eligibility_answer === "no") setState("answered_no");
      else if (d.occupancy_status === "tenant") setState("tenant");
      else if (d.eligibility_answer && d.occupancy_status === "owner") setState(d.concerns_answered ? "answered_yes" : "concerns");
      else if (d.eligibility_answer) setState("ask_occupancy");
      else setState("ready");
    };
    const publicFetch = () =>
      api.get(`/public/check/${token}`).then(({ data: d }) => applyData(d, true)).catch(() => setState("notfound"));
    // StrictMode double-mount guard — one visit must count as ONE scan.
    const fetchKey = `${token}|${wantPreview}`;
    if (fetchedFor.current !== fetchKey) {
      fetchedFor.current = fetchKey;
      if (wantPreview) {
        api.get(`/admin/property-campaigns/preview-check/${token}`)
          .then(({ data: d }) => applyData(d, false))
          .catch(() => publicFetch());
      } else {
        publicFetch();
      }
    }
    return () => meta.remove();
  }, [token, wantPreview, loadSlots]);

  const answer = async (a) => {
    if (isPreview) { toast.info("Admin preview — answers are disabled so campaign data stays clean"); return; }
    setBusy(true);
    try {
      await api.post(`/public/check/${token}/answer`, { answer: a });
      setState(a === "no" ? "answered_no" : "ask_occupancy");
    } catch { toast.error("Something went wrong — please try again"); }
    finally { setBusy(false); }
  };

  const answerOccupancy = async (occ) => {
    if (isPreview) { toast.info("Admin preview — answers are disabled so campaign data stays clean"); return; }
    setBusy(true);
    try {
      await api.post(`/public/check/${token}/occupancy`, { occupancy: occ });
      setState(occ === "owner" ? "concerns" : "tenant");
    } catch { toast.error("Something went wrong — please try again"); }
    finally { setBusy(false); }
  };

  const toggleConcern = (key) => setConcerns((cur) => (cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]));

  const submitConcerns = async () => {
    if (isPreview) { toast.info("Admin preview — answers are disabled so campaign data stays clean"); return; }
    if (busy || !concerns.length) return;
    setBusy(true);
    try {
      await api.post(`/public/check/${token}/concerns`, { concerns, other_text: otherText || null });
      setState("answered_yes");
    } catch { toast.error("Something went wrong — please try again"); }
    finally { setBusy(false); }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (isPreview) { toast.info("Admin preview — submissions are disabled so campaign data stays clean"); return; }
    setBusy(true);
    try {
      const { data: r } = await api.post(`/public/check/${token}/lead`, form);
      pushFormEvent("contact_form_submit", {
        form_name: "property_campaign_lead",
        form_type: "campaign_lead",
        campaign_source: "property_campaign",
      });
      if (r.ebook_url) setEbookUrl(r.ebook_url);
      await loadSlots();
    } catch (err) {
      toast.error(err.response?.status === 422 ? "Please check your details" : "Something went wrong");
    } finally { setBusy(false); }
  };

  const bookSlot = async (s) => {
    if (isPreview) { toast.info("Admin preview — bookings are disabled so campaign data stays clean"); return; }
    if (busy) return;
    setBusy(true);
    try {
      const endpoint = resched ? "reschedule" : "book";
      const { data: r } = await api.post(`/public/check/${token}/${endpoint}`,
        { slot_id: s.id, start_time: s.start_time });
      setAppt(r.appointment);
      setState("booked");
      if (!resched) {
        pushFormEvent("booking_form_submit", {
          form_name: "property_campaign_booking",
          form_type: "booking",
          booking_type: "property_campaign",
          campaign_source: "property_campaign",
          campaign_ref: r.appointment?.campaign_id || null,
        });
      }
      if (resched) { setResched(false); toast.success("Your appointment has been updated"); }
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error("Sorry — that time has just been taken. Here are the remaining times.");
        await loadSlots();
      } else {
        toast.error("Something went wrong — please try again");
      }
    } finally { setBusy(false); }
  };

  const startReschedule = async () => {
    if (isPreview) { toast.info("Admin preview — changes are disabled so campaign data stays clean"); return; }
    setBusy(true);
    try {
      const { data: s } = await api.get(`/public/check/${token}/slots`);
      if (!(s.slots || []).length) { toast.info("No other times are currently available — please contact us to arrange a change"); return; }
      setSlots(s.slots); setResched(true); setState("choose_slot");
    } catch { toast.error("Something went wrong — please try again"); }
    finally { setBusy(false); }
  };

  const cancelAppt = async () => {
    if (isPreview) { toast.info("Admin preview — changes are disabled so campaign data stays clean"); return; }
    if (!window.confirm("Cancel your Water Health Check appointment?\n\nYou can book a new time afterwards if you change your mind.")) return;
    setBusy(true);
    try {
      await api.post(`/public/check/${token}/cancel`);
      setAppt(null); setResched(false);
      toast.success("Your appointment has been cancelled");
      await loadSlots();
    } catch { toast.error("Something went wrong — please try again"); }
    finally { setBusy(false); }
  };

  const addr = data ? `${data.street_address}` : "";
  const locality = data ? `${data.suburb} ${data.state} ${data.postcode}` : "";

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center px-4 py-8">
      <div className="w-full max-w-md">
        {isPreview && (
          <div data-testid="check-preview-banner" className="mb-4 rounded-2xl bg-amber-50 border border-amber-300 px-4 py-2.5 text-xs font-bold text-amber-800 flex items-center gap-2">
            <Eye className="w-4 h-4" /> ADMIN PREVIEW — this visit is not counted as a scan
          </div>
        )}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 font-bold text-slate-900 text-xl"><Droplets className="w-6 h-6 text-cyan-600" /> AWARE Water Testing</div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-cyan-700 mt-1">Complimentary Water Health Check</div>
        </div>

        {state === "loading" && <div className="text-center text-slate-500 py-16"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>}

        {state === "notfound" && (
          <div data-testid="check-not-found" className="bg-white rounded-3xl border border-slate-200 p-6 text-center text-slate-700">
            This invitation link isn't valid. Please check the QR code on your letter or contact AWARE Water Testing.
          </div>
        )}

        {data && state !== "notfound" && state !== "loading" && (
          <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
            <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider">This invitation relates to:</div>
            <div data-testid="check-address" className="mt-1 text-lg font-bold text-slate-900">{addr}</div>
            <div className="text-slate-600">{locality}</div>

            {state === "ready" && (
              <div className="mt-6">
                <h1 className="text-base font-bold text-slate-900">Check if this property is eligible</h1>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">We're currently offering a limited number of complimentary Water Health Checks to properties in your area that rely on rainwater, bore water or another private water supply.</p>
                <p className="mt-4 text-[15px] font-semibold text-slate-900">Does this property use rainwater or bore water for household use?</p>
                <div className="mt-4 grid gap-2">
                  <button data-testid="check-answer-yes" disabled={busy} onClick={() => answer("yes")} className="w-full py-3.5 rounded-full bg-cyan-600 hover:bg-cyan-700 text-white font-bold">YES</button>
                  <button data-testid="check-answer-not-sure" disabled={busy} onClick={() => answer("not_sure")} className="w-full py-3.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold">NOT SURE</button>
                  <button data-testid="check-answer-no" disabled={busy} onClick={() => answer("no")} className="w-full py-3.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold">NO</button>
                </div>
              </div>
            )}

            {state === "ask_occupancy" && (
              <div data-testid="check-occupancy" className="mt-6">
                <p className="text-[15px] font-semibold text-slate-900">Do you own or rent this property?</p>
                <div className="mt-4 grid gap-2">
                  <button data-testid="check-occupancy-owner" disabled={busy} onClick={() => answerOccupancy("owner")} className="w-full py-3.5 rounded-full bg-cyan-600 hover:bg-cyan-700 text-white font-bold">OWNER</button>
                  <button data-testid="check-occupancy-tenant" disabled={busy} onClick={() => answerOccupancy("tenant")} className="w-full py-3.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold">TENANT</button>
                </div>
              </div>
            )}

            {state === "tenant" && (
              <div data-testid="check-tenant" className="mt-6 rounded-2xl bg-slate-50 border border-slate-200 p-4 text-sm text-slate-700 leading-relaxed">
                <div className="font-bold text-slate-900 mb-1">Thanks for checking</div>
                This complimentary Water Health Check program is currently available to property owners, as any recommendations may involve the property's rainwater tank, filtration, treatment system or ongoing maintenance.
                <div className="mt-2">If you rent this property, we recommend passing the invitation on to the property owner.</div>
              </div>
            )}

            {state === "concerns" && (
              <div data-testid="check-concerns" className="mt-6">
                <p className="text-[15px] font-semibold text-slate-900">Is there anything about your water that you're concerned about?</p>
                <p className="mt-1.5 text-xs text-slate-500">Tap all that apply — this helps your technician prepare for the visit.</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {CONCERN_OPTS.map(([key, label]) => (
                    <button key={key} type="button" data-testid={`check-concern-${key}`} onClick={() => toggleConcern(key)}
                      className={`px-3.5 py-2.5 rounded-full text-sm font-semibold border-2 transition-colors text-left ${concerns.includes(key) ? "bg-cyan-600 border-cyan-600 text-white" : "border-slate-200 bg-white text-slate-600"}`}>
                      {label}
                    </button>
                  ))}
                </div>
                {concerns.includes("other") && (
                  <textarea data-testid="check-concerns-other" value={otherText} onChange={(e) => setOtherText(e.target.value)} maxLength={300}
                    placeholder="Tell us briefly what you've noticed" className="mt-3 w-full rounded-xl border border-slate-300 px-4 py-3 text-[15px]" rows={2} />
                )}
                <button data-testid="check-concerns-continue" disabled={busy || concerns.length === 0} onClick={submitConcerns}
                  className="mt-4 w-full py-3.5 rounded-full bg-cyan-600 hover:bg-cyan-700 text-white font-bold disabled:opacity-50">
                  {busy ? "Saving…" : "Continue"}
                </button>
              </div>
            )}

            {state === "answered_yes" && (
              <form onSubmit={submitLead} className="mt-6">
                <div data-testid="check-good-news" className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4">
                  <div className="font-bold text-emerald-900">Good news</div>
                  <div className="text-sm text-emerald-800 mt-1">{addr} may qualify. We just need a few details to confirm your eligibility.</div>
                </div>
                <div className="mt-4 space-y-3">
                  <input data-testid="check-first-name" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="First Name" className="w-full rounded-xl border border-slate-300 px-4 py-3 text-[15px]" />
                  <input data-testid="check-last-name" required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder="Last Name" className="w-full rounded-xl border border-slate-300 px-4 py-3 text-[15px]" />
                  <input data-testid="check-mobile" required minLength={8} value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="Mobile" type="tel" className="w-full rounded-xl border border-slate-300 px-4 py-3 text-[15px]" />
                  <input data-testid="check-email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" type="email" className="w-full rounded-xl border border-slate-300 px-4 py-3 text-[15px]" />
                </div>
                <button data-testid="check-submit-lead" disabled={busy} type="submit" className="mt-4 w-full py-3.5 rounded-full bg-cyan-600 hover:bg-cyan-700 text-white font-bold">
                  {busy ? "Submitting…" : "Check My Eligibility"}
                </button>
                <p data-testid="check-consent-note" className="mt-3 text-[11px] text-slate-400 leading-relaxed">
                  We'll use your details to email you your free guide, contact you about this Water Health Check and send appointment confirmations or reminders. We won't opt you into unrelated marketing.
                </p>
              </form>
            )}

            {state === "answered_no" && (
              <div data-testid="check-not-eligible" className="mt-6 rounded-2xl bg-slate-50 border border-slate-200 p-4 text-sm text-slate-700 leading-relaxed">
                <div className="font-bold text-slate-900 mb-1">Thanks for checking</div>
                This particular Water Health Check program is intended for properties using rainwater, bore water or another private water supply for household use.
              </div>
            )}

            {state === "choose_slot" && (
              <div data-testid="check-choose-slot" className="mt-6">
                <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4">
                  <div className="font-bold text-emerald-900">{resched ? "Your booking is confirmed — just picking a new time" : "Congratulations — your property qualifies!"}</div>
                  <div className="text-sm text-emerald-800 mt-1">{addr}, {data?.suburb}</div>
                </div>
                <h2 className="mt-5 text-[15px] font-bold text-slate-900">{resched ? "Choose a new time for your Water Health Check." : "Choose a convenient time for your complimentary Water Health Check."}</h2>
                <p className="mt-1.5 text-xs text-slate-500 leading-relaxed">We're already working in your area on these dates, so we've made a limited number of local appointment times available.</p>
                {resched && (
                  <button data-testid="check-keep-time" onClick={() => { setResched(false); setState("booked"); }}
                    className="mt-2 text-xs font-semibold text-slate-500 underline">Keep my current time instead</button>
                )}
                <div className="mt-4">
                  {(() => {
                    const days = [];
                    for (const s of slots) {
                      const key = s.date || s.date_display;
                      let d = days.find((x) => x.key === key);
                      if (!d) { d = { key, date: s.date, date_display: s.date_display, times: [] }; days.push(d); }
                      d.times.push(s);
                    }
                    const shortDay = (d) => {
                      if (!d.date) return { top: d.date_display, sub: "" };
                      const dt = new Date(`${d.date}T00:00:00`);
                      return {
                        top: dt.toLocaleDateString("en-AU", { weekday: "short" }),
                        sub: dt.toLocaleDateString("en-AU", { day: "numeric", month: "short" }),
                      };
                    };
                    const visible = showAllDates ? days : days.slice(0, 4);
                    const sel = days.find((x) => x.key === selDate);
                    return (
                      <>
                        <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">1. Pick a day</div>
                        <div className="grid grid-cols-4 gap-2">
                          {visible.map((d) => {
                            const sd = shortDay(d);
                            const active = selDate === d.key;
                            return (
                              <button key={d.key} type="button" data-testid={`check-slot-date-${d.key}`}
                                onClick={() => { setSelDate(d.key); setSelSlot(null); }}
                                className={`rounded-2xl border-2 px-1 py-2.5 text-center transition-colors ${active ? "bg-cyan-600 border-cyan-600 text-white" : "bg-white border-slate-200 text-slate-700 hover:border-cyan-300"}`}>
                                <div className="text-xs font-bold uppercase">{sd.top}</div>
                                <div className={`text-sm font-extrabold ${active ? "text-white" : "text-slate-900"}`}>{sd.sub}</div>
                                <div className={`text-[10px] font-semibold ${active ? "text-cyan-100" : "text-slate-400"}`}>{d.times.length} time{d.times.length === 1 ? "" : "s"}</div>
                              </button>
                            );
                          })}
                        </div>
                        {!showAllDates && days.length > 4 && (
                          <button type="button" data-testid="check-more-dates" onClick={() => setShowAllDates(true)}
                            className="mt-2 w-full py-2 rounded-full border border-slate-200 text-xs font-bold text-slate-600 hover:border-cyan-300">
                            More dates ({days.length - 4})
                          </button>
                        )}
                        {sel && (
                          <div className="mt-4" data-testid="check-slot-times">
                            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">2. Pick a time — {sel.date_display}</div>
                            <div className="grid grid-cols-2 gap-2">
                              {sel.times.map((s) => {
                                const active = selSlot && selSlot.id === s.id && selSlot.start_time === s.start_time;
                                return (
                                  <button key={`${s.id}-${s.start_time}`} type="button" data-testid={`check-slot-${s.id}-${s.start_time}`}
                                    onClick={() => setSelSlot(s)}
                                    className={`rounded-xl border-2 px-2 py-2.5 text-sm font-bold transition-colors ${active ? "bg-cyan-600 border-cyan-600 text-white" : "bg-white border-slate-200 text-slate-800 hover:border-cyan-300"}`}>
                                    {s.start_display}
                                    {s.remaining === 1 && <div className={`text-[10px] font-semibold ${active ? "text-amber-200" : "text-amber-600"}`}>Last spot</div>}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {selSlot && (
                          <>
                            {/* Jun 2026 — customers book an ARRIVAL WINDOW, never a
                                guaranteed exact time. */}
                            <div className="mt-4 rounded-xl border-2 border-cyan-200 bg-cyan-50/60 px-3 py-2.5"
                              data-testid="check-arrival-window">
                              <div className="text-sm font-bold text-cyan-900">
                                Your booked arrival window will be {selSlot.arrival_window_display}.
                              </div>
                              <p className="mt-1 text-xs leading-relaxed text-cyan-900/70">
                                Our technicians work across multiple properties each day, so arrival
                                times can occasionally change due to travel, previous jobs or
                                unexpected delays. If your arrival window needs to change, we’ll
                                endeavour to give you as much notice as possible. We’ll also send you
                                a reminder by text and email the day before.
                              </p>
                            </div>
                            <button data-testid="check-book-slot-confirm" disabled={busy} onClick={() => bookSlot(selSlot)}
                              className="mt-3 w-full py-3.5 rounded-full bg-cyan-600 hover:bg-cyan-700 text-white font-bold disabled:opacity-60">
                              {busy ? "Confirming your booking… this can take a few seconds"
                                : `Confirm ${selSlot.arrival_window_display}`}
                            </button>
                          </>
                        )}
                        {!sel && <p className="mt-3 text-xs text-slate-400">Tap a day above to see the available times.</p>}
                      </>
                    );
                  })()}
                </div>
              </div>
            )}

            {state === "completed" && appt && (
              <div data-testid="check-completed" className="mt-6">
                <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto" />
                <h2 className="mt-2 text-center text-lg font-bold text-slate-900">Your Water Health Check is complete</h2>
                <div className="mt-4 rounded-2xl bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-900">
                  <div className="font-bold">{addr}, {data?.suburb}</div>
                  <div className="mt-1">Visited on {appt.date_display}{appt.partner_name ? ` by ${appt.partner_name}` : ""}.</div>
                </div>
                <p className="mt-3 text-center text-sm text-slate-600">
                  Your inspection report is emailed to you once it's finalised — check your inbox.<br />
                  Questions? Just reply to any of our emails.
                </p>
                {ebookUrl && (
                  <div className="mt-5 text-center">
                    <a data-testid="check-completed-ebook-download" href={`${ebookUrl}?src=completed_page`}
                      className="inline-block px-5 py-2.5 rounded-full bg-cyan-600 text-white font-bold text-sm">Download Your Free Guide</a>
                  </div>
                )}
              </div>
            )}

            {state === "booked" && appt && (
              <div data-testid="check-booked" className="mt-6">
                <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-5 text-center">
                  <CalendarCheck className="w-8 h-8 text-emerald-600 mx-auto" />
                  <div className="mt-2 text-lg font-bold text-emerald-900">You're booked</div>
                </div>
                <div className="mt-4 rounded-2xl border border-slate-200 p-4 text-sm">
                  <div className="font-bold text-slate-900 mb-2">Water Health Check</div>
                  <div className="grid grid-cols-[90px_1fr] gap-y-2 text-slate-700">
                    <span className="text-slate-400 font-semibold">Property:</span>
                    <span data-testid="check-booked-property">{addr}<br />{locality}</span>
                    <span className="text-slate-400 font-semibold">Date:</span>
                    <span data-testid="check-booked-date" className="font-semibold">{appt.date_display}</span>
                    <span className="text-slate-400 font-semibold">Arrival window:</span>
                    <span data-testid="check-booked-time" className="font-semibold">{appt.arrival_window_display}</span>
                    {appt.partner_name && (<>
                      <span className="text-slate-400 font-semibold">Team:</span>
                      <span>{appt.partner_name}</span>
                    </>)}
                  </div>
                </div>
                <p className="mt-3 text-center text-sm text-slate-600">We've sent the details to your email.<br />We'll also send you a reminder by text and email the day before your booking.</p>
                {appt.arrival_window_notice && (
                  <p className="mt-2 text-center text-xs leading-relaxed text-slate-500"
                    data-testid="check-booked-arrival-notice">
                    {appt.arrival_window_notice}
                  </p>
                )}
                <div className="mt-4 rounded-2xl border border-slate-200 p-4 text-center" data-testid="check-manage-booking">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Need to make a change?</div>
                  <div className="mt-2 flex flex-wrap justify-center gap-2">
                    <button data-testid="check-reschedule-btn" disabled={busy} onClick={startReschedule}
                      className="px-4 py-2 rounded-full border border-cyan-600 text-cyan-700 text-sm font-bold hover:bg-cyan-50 disabled:opacity-60">
                      Choose a different time
                    </button>
                    <button data-testid="check-cancel-btn" disabled={busy} onClick={cancelAppt}
                      className="px-4 py-2 rounded-full border border-slate-300 text-slate-500 text-sm font-semibold hover:bg-slate-50 disabled:opacity-60">
                      Cancel appointment
                    </button>
                  </div>
                </div>
                {ebookUrl && (
                  <div data-testid="check-booked-ebook" className="mt-5 rounded-2xl border border-cyan-100 bg-cyan-50/60 p-4 text-center">
                    <div className="text-xs font-bold uppercase tracking-wider text-cyan-700">While you wait — your free guide</div>
                    <a href={`${ebookUrl}?src=booking_confirmation`}>
                      <img src={`${process.env.REACT_APP_BACKEND_URL}/api/public/assets/ebook-mockup?variant=mobile`}
                        alt="The Homeowner's Guide to Healthy Tank Water – AWARE Water Testing"
                        className="mx-auto mt-2 w-full max-w-[200px] rounded-xl" data-testid="check-booked-ebook-mockup" />
                    </a>
                    <div className="mt-1 text-[13px] font-bold text-slate-900">The Homeowner's Guide to Healthy Tank Water</div>
                    <div className="text-[11px] text-slate-500">We've emailed you a copy, or you can download it now.</div>
                    <a data-testid="check-booked-ebook-download" href={`${ebookUrl}?src=booking_confirmation`}
                      className="mt-2 inline-block px-5 py-2.5 rounded-full bg-cyan-600 text-white font-bold text-sm">Download Your Free Guide</a>
                  </div>
                )}
              </div>
            )}

            {state === "lead_done" && (
              <div data-testid="check-lead-done" className="mt-6 rounded-2xl bg-emerald-50 border border-emerald-200 p-5 text-center">
                <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto" />
                <div className="mt-2 font-bold text-emerald-900">Thanks — we've received your request</div>
                <div className="text-sm text-emerald-800 mt-1">Your details have been received for:<br /><b>{addr}, {data?.suburb}</b></div>
                <div className="text-sm text-emerald-800 mt-2">A member of the local water services team will be in touch to confirm the Water Health Check and available times.</div>
                {ebookUrl && (
                  <a data-testid="check-ebook-download-done" href={`${ebookUrl}?src=web_cta`} className="mt-4 inline-block px-5 py-2.5 rounded-full bg-cyan-600 text-white font-bold text-sm">Download Your Free Guide</a>
                )}
              </div>
            )}

            {data?.aware4_offer && (
              <div data-testid="check-aware4-offer" className="mt-6 rounded-2xl border border-teal-200 bg-teal-50 p-4">
                <div className="text-[10px] font-bold uppercase tracking-wider text-teal-700">Your letter offer — this property only</div>
                <div className="mt-1 font-bold text-slate-900">
                  {data.aware4_offer.test_name} Laboratory Water Test — ${Number(data.aware4_offer.price).toFixed(0)}
                  <span className="ml-1.5 text-slate-400 line-through font-semibold text-sm">${Number(data.aware4_offer.normal_price).toFixed(0)}</span>
                </div>
                <div className="mt-1 text-xs text-slate-600 leading-relaxed">
                  Single-use code <b data-testid="check-aware4-code" className="font-mono text-teal-800">{data.aware4_offer.discount_code}</b> — the $99 price is applied automatically at checkout for this property.
                </div>
                <a data-testid="check-aware4-book" href={`/book?test=basic&discount=${data.aware4_offer.discount_code}`}
                  className="mt-3 inline-block px-5 py-2.5 rounded-full bg-teal-600 hover:bg-teal-700 text-white font-bold text-sm">
                  Book the $99 lab test
                </a>
              </div>
            )}
          </div>
        )}
        <div className="text-center text-[11px] text-slate-400 mt-6">No obligation. No payment required to check.</div>
      </div>
    </div>
  );
}
